import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { socialKeys, restaurantKeys } from '@/api/keys';
import { digestKeys } from '@/api/keys/digestKeys';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { appsFlyerService } from '@/lib/appsflyer';
import { useToast } from '@chakra-ui/react';
import type {
  Post,
  Comment,
  CheckIn,
  Notification,
  NotificationPreferences,
  BlockedUser,
  Report,
  RelationshipRequest,
  CreateCommentRequest,
  CreateCheckInRequest,
  CreateReportRequest,
  BlockUserRequest,
  UnblockResponse,
  ReactionType,
  ReactionSummary,
  Story,
  CreateStoryRequest,
  CreatePollRequest,
  UserSearchResult,
  SendOTPRequest,
  SendOTPResponse,
  VerifyOTPRequest,
  VerifyOTPResponse,
  ContactsMatchAllRequest,
  ContactsMatchAllResponse,
  ReportMenuItemRequest,
  ReportRestaurantRequest,
} from '@/types/social';

// ============ Posts ============

/**
 * Hook to create a new post
 */
export function useCreatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postData: {
      content: string;
      restaurants?: number[];
      restaurantLists?: number[];
      photoUrls?: string[];
      poll?: CreatePollRequest;
    }) =>
      apiFetch<Post>('/social/posts/', {
        method: 'POST',
        body: postData,
      }),
    onSuccess: (_, variables) => {
      // Track post creation
      analytics.capture(AnalyticsEvents.POST_CREATED, {
        has_images: (variables.photoUrls?.length ?? 0) > 0,
        has_poll: !!variables.poll,
        restaurant_count: variables.restaurants?.length ?? 0,
      });

      // refetchType: 'active' ensures the feed immediately re-fetches
      // rather than waiting for the next time the query comes into focus.
      queryClient.invalidateQueries({ queryKey: socialKeys.posts(), refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed(), refetchType: 'active' });
    },
  });
}

/**
 * Hook to delete a post
 */
export function useDeletePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: number) =>
      apiFetch<void>(`/social/posts/${postId}/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.posts() });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

/**
 * Hook to like a post
 */
export function useLikePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: number) =>
      apiFetch<void>(`/social/posts/${postId}/like/`, { method: 'POST' }),
    onSuccess: (_, postId) => {
      // Track post liked
      analytics.capture(AnalyticsEvents.POST_LIKED, {
        post_id: postId,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.posts() });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

/**
 * Hook to unlike a post
 */
export function useUnlikePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: number) =>
      apiFetch<void>(`/social/posts/${postId}/unlike/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.posts() });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

/**
 * Hook to share a post
 */
export function useSharePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: number) =>
      apiFetch<void>(`/social/posts/${postId}/share/`, { method: 'POST' }),
    onSuccess: (_, postId) => {
      // Track post shared
      analytics.capture(AnalyticsEvents.POST_SHARED, {
        post_id: postId,
        share_method: 'in_app',
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.posts() });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

// ============ Reactions (Posts) ============

/**
 * Hook to add or change a reaction on a post
 */
export function useReactToPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, reactionType }: { urlKey: string; reactionType: ReactionType }) =>
      apiFetch<ReactionSummary>(`/social/posts/${urlKey}/react/`, {
        method: 'POST',
        body: { reaction_type: reactionType },
      }),
    onSuccess: (_, variables) => {
      // Track post reacted
      analytics.capture(AnalyticsEvents.POST_REACTED, {
        post_url_key: variables.urlKey,
        reaction_type: variables.reactionType,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.posts() });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

/**
 * Hook to remove a reaction from a post
 */
export function useUnreactToPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlKey: string) =>
      apiFetch<void>(`/social/posts/${urlKey}/unreact/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.posts() });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

// ============ Activities ============

/**
 * Hook to like an activity
 */
export function useLikeActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId: number | string) =>
      apiFetch<void>(`/social/feed/${activityId}/like/`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

/**
 * Hook to unlike an activity
 */
export function useUnlikeActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId: number | string) =>
      apiFetch<void>(`/social/feed/${activityId}/unlike/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

/**
 * Hook to share an activity
 */
export function useShareActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId: number | string) =>
      apiFetch<void>(`/social/feed/${activityId}/share/`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

// ============ Reactions (Activities) ============

/**
 * Hook to add or change a reaction on a feed activity
 */
export function useReactToActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, reactionType }: { urlKey: string; reactionType: ReactionType }) =>
      apiFetch<ReactionSummary>(`/social/feed/${urlKey}/react/`, {
        method: 'POST',
        body: { reaction_type: reactionType },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

/**
 * Hook to remove a reaction from a feed activity
 */
export function useUnreactToActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlKey: string) =>
      apiFetch<void>(`/social/feed/${urlKey}/unreact/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

// ============ Comments ============

/**
 * Hook to create a comment on a post
 */
export function useCreateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, data }: { postId: number; data: CreateCommentRequest }) =>
      apiFetch<Comment>(`/social/posts/${postId}/comments/create/`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: (_, variables) => {
      // Track comment created
      analytics.capture(AnalyticsEvents.COMMENT_CREATED, {
        parent_type: 'post',
        parent_id: variables.postId,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.comments() });
      queryClient.invalidateQueries({ queryKey: socialKeys.posts() });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

/**
 * Hook to create a comment on an activity
 */
export function useCreateActivityComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      activityId,
      data,
    }: {
      activityId: number | string;
      data: CreateCommentRequest;
    }) =>
      apiFetch<Comment>(`/social/feed/${activityId}/comments/create/`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: (_, variables) => {
      // Track comment created
      analytics.capture(AnalyticsEvents.COMMENT_CREATED, {
        parent_type: 'activity',
        parent_id: variables.activityId,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.comments() });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

/**
 * Hook to delete a comment
 */
export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: number) =>
      apiFetch<void>(`/social/comments/${commentId}/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.comments() });
      queryClient.invalidateQueries({ queryKey: socialKeys.posts() });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

// ============ Check-ins ============

/**
 * Hook to create a check-in
 */
export function useCreateCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCheckInRequest) =>
      apiFetch<CheckIn>('/social/checkins/', {
        method: 'POST',
        body: data,
      }),
    onSuccess: (data, variables) => {
      // Track check-in
      analytics.capture(AnalyticsEvents.CHECK_IN_CREATED, {
        restaurant_id: variables.restaurant,
      });

      // AppsFlyer: Track check-in event
      appsFlyerService.logCheckIn(
        variables.restaurant,
        data.restaurant?.name ?? 'Unknown'
      );

      queryClient.invalidateQueries({ queryKey: socialKeys.checkIns() });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
      // Keep the cached profile current so a later open (or the open page) does
      // not flash pre-check-in data.
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.detail(variables.restaurant),
        exact: true,
      });
    },
  });
}

// ============ Relationships ============

/**
 * Hook to send a friend request
 */
export function useSendFriendRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RelationshipRequest) =>
      apiFetch<void>('/social/relationships/friend-request/', {
        method: 'POST',
        body: request,
      }),
    onSuccess: (_, variables) => {
      // Track friend request sent
      analytics.capture(AnalyticsEvents.FRIEND_REQUEST_SENT, {
        target_user_id: variables.userId,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.friends() });
      queryClient.invalidateQueries({ queryKey: socialKeys.users() });
    },
  });
}

/**
 * Hook to accept a friend request.
 *
 * Applies an optimistic update so the notification immediately shows
 * "accepted" / read state in the UI while the server processes the request.
 * If the server returns a 4xx/5xx the update is rolled back to the prior state.
 *
 * Backend B13: the accept endpoint now also marks the associated notification
 * as is_read=True, so the optimistic read-mark matches the server's final state.
 */
export function useAcceptFriendRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: number) =>
      apiFetch<void>(`/social/relationships/${requestId}/accept/`, { method: 'POST' }),

    onMutate: async (requestId: number) => {
      // Cancel any in-flight refetches so they don't overwrite our optimistic update.
      // Use notificationsList key (not the broader notifications prefix) to avoid
      // accidentally cancelling unread-count/preferences queries.
      await queryClient.cancelQueries({ queryKey: socialKeys.notificationsList() });

      // Snapshot current notification list caches for rollback.
      const previousNotificationCaches = queryClient.getQueriesData<import('@/types/social').NotificationsResponse>({
        queryKey: socialKeys.notificationsList(),
      });

      // Optimistically update every cached notifications page:
      // - Set the relationship status to 'accepted' on matching contentData
      // - Mark the notification as read (mirrors what B13 backend now does)
      queryClient.setQueriesData<import('@/types/social').NotificationsResponse>(
        { queryKey: socialKeys.notificationsList() },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            results: oldData.results.map((notification) => {
              const isMatchingRequest =
                notification.notificationType === 'friend_request' &&
                notification.contentData?.id === requestId &&
                notification.contentData?.status === 'pending';

              if (!isMatchingRequest) return notification;

              return {
                ...notification,
                isRead: true,
                readAt: new Date().toISOString(),
                contentData: notification.contentData
                  ? { ...notification.contentData, status: 'accepted' as const }
                  : notification.contentData,
              };
            }),
          };
        }
      );

      // Return snapshot so onError can roll back.
      return { previousNotificationCaches };
    },

    onSuccess: (_, requestId) => {
      // Track friend request accepted
      analytics.capture(AnalyticsEvents.FRIEND_REQUEST_ACCEPTED, {
        requester_id: requestId,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.friends() });
      queryClient.invalidateQueries({ queryKey: socialKeys.users() });
      // Refetch notifications to sync final server state (read status + status field).
      queryClient.invalidateQueries({ queryKey: socialKeys.notifications() });
    },

    onError: (_error, _requestId, context) => {
      // Roll back the optimistic update if the server rejected the request.
      if (context?.previousNotificationCaches) {
        for (const [queryKey, data] of context.previousNotificationCaches) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
  });
}

/**
 * Hook to reject a friend request
 */
export function useRejectFriendRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: number) =>
      apiFetch<void>(`/social/relationships/${requestId}/reject/`, { method: 'POST' }),
    onSuccess: (_, requestId) => {
      // Track friend request rejected
      analytics.capture(AnalyticsEvents.FRIEND_REQUEST_REJECTED, {
        requester_id: requestId,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.friends() });
      queryClient.invalidateQueries({ queryKey: socialKeys.users() });
      queryClient.invalidateQueries({ queryKey: socialKeys.notifications() });
    },
  });
}

/**
 * D51e FR-C10 / D51f Phase 2 — a blocked or unfriended member's content must
 * not linger in ANY cached Digest issue: the current issue, the archive
 * list's cover teasers, or a cached past issue (`detail(id)`).
 *
 * Both mutations typically run from a profile page, where the digest queries
 * are INACTIVE — a plain invalidate would only mark them stale, so
 * `refetchType: 'all'` forces the refetch now rather than leaving a brief
 * flash of the removed member's content on the next visit. Inactive
 * `detail(id)` entries are REMOVED first (and so never refetched): past
 * issues are cheap to re-read (`staleTime: 0`, refetch on mount) and there is
 * no reason to spend requests warming ones nobody is viewing. `detail`
 * removal is safe; `removeQueries` on `current()` is NOT — an emptied cache
 * would incorrectly mount `GoingToPressState` and fire a spurious generate
 * request — which is why `current()` is the one entry refetched in place.
 * The archive gets the `detail` treatment (inactive: removed; active: reset to
 * page 1), see the function body.
 */
function invalidateDigestForRelationshipChange(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: digestKeys.details(), type: 'inactive' });
  // The archive is an INFINITE query: a plain refetch re-requests EVERY page it has loaded (a member who paged deep
  // through All Issues would fire one request per page, sequentially, for a list nobody is looking at). So, like
  // `detail(id)`, an inactive archive is removed (the next visit fetches page 1 fresh: it is only ever mounted by
  // All Issues, which shows a skeleton while pending, so there is no flash), and an ACTIVE one (the member is on
  // All Issues right now) is RESET, which drops its pages and refetches page 1 only. Either way none of the removed
  // member's cover teasers survive.
  queryClient.removeQueries({ queryKey: digestKeys.archive(), type: 'inactive' });
  void queryClient.resetQueries({ queryKey: digestKeys.archive() });
  // Everything else (current, still-cached past issues) is refetched in place; the archive was handled above and is
  // excluded by key so it is not fetched a second time. Same predicate as `refreshDigestData` (billingRefresh).
  queryClient.invalidateQueries({
    queryKey: digestKeys.all,
    refetchType: 'all',
    predicate: (query) => query.queryKey[1] !== 'archive',
  });
}

/**
 * Hook to unfriend a user
 */
export function useUnfriend() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) =>
      apiFetch<void>(`/social/relationships/unfriend/${userId}/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.friends() });
      queryClient.invalidateQueries({ queryKey: socialKeys.users() });
      invalidateDigestForRelationshipChange(queryClient);
      // FeedTeaserCard (D51e) reads the social feed — an unfriended
      // member's activity shouldn't linger there for up to 5 minutes.
      queryClient.invalidateQueries({ queryKey: socialKeys.feed(), refetchType: 'all' });
    },
  });
}

/**
 * Hook to follow a user
 */
export function useFollowUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RelationshipRequest) =>
      apiFetch<void>('/social/relationships/follow/', {
        method: 'POST',
        body: request,
      }),
    onSuccess: (_, variables) => {
      // Track user followed
      analytics.capture(AnalyticsEvents.USER_FOLLOWED, {
        target_user_id: variables.userId,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.following() });
      queryClient.invalidateQueries({ queryKey: socialKeys.users() });
    },
  });
}

/**
 * Hook to unfollow a user
 */
export function useUnfollowUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) =>
      apiFetch<void>(`/social/relationships/unfollow/${userId}/`, { method: 'DELETE' }),
    onSuccess: (_, userId) => {
      // Track user unfollowed
      analytics.capture(AnalyticsEvents.USER_UNFOLLOWED, {
        target_user_id: userId,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.following() });
      queryClient.invalidateQueries({ queryKey: socialKeys.users() });
    },
  });
}

// ============ Block/Unblock ============

/**
 * Hook to block a user
 * Invalidates all relevant caches since blocking removes relationships
 */
export function useBlockUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BlockUserRequest) =>
      apiFetch<BlockedUser>('/social/users/block/', {
        method: 'POST',
        body: request,
      }),
    onSuccess: (_, variables) => {
      // Track user blocked
      analytics.capture(AnalyticsEvents.USER_BLOCKED, {
        blocked_user_id: variables.user_id,
      });

      // Invalidate relationship caches
      queryClient.invalidateQueries({ queryKey: socialKeys.friends() });
      queryClient.invalidateQueries({ queryKey: socialKeys.following() });
      queryClient.invalidateQueries({ queryKey: socialKeys.followers() });
      queryClient.invalidateQueries({ queryKey: socialKeys.blocked() });
      // Invalidate user profile caches (blocked user's profile data)
      queryClient.invalidateQueries({ queryKey: socialKeys.users() });
      // Invalidate feed since blocked user's content is removed. `refetchType:
      // 'all'` (as in `useUnfriend`): FeedTeaserCard reads the feed while it may
      // be unobserved, and the default 'active' would leave it stale.
      queryClient.invalidateQueries({ queryKey: socialKeys.feed(), refetchType: 'all' });
      // FR-C10: a blocked member's content shouldn't linger in a stale
      // cached issue.
      invalidateDigestForRelationshipChange(queryClient);
    },
  });
}

/**
 * Hook to unblock a user
 */
export function useUnblockUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) =>
      apiFetch<UnblockResponse>(`/social/users/unblock/${userId}/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.blocked() });
    },
  });
}

// ============ Reports ============

/**
 * Hook to create a report
 */
export function useCreateReport() {
  return useMutation({
    mutationFn: (request: CreateReportRequest) =>
      apiFetch<Report>('/social/reports/', {
        method: 'POST',
        body: request,
      }),
  });
}

/**
 * Hook to report a menu item via POST /menu-item-reports/
 * Shows a success or error toast on completion. No cache invalidation needed.
 */
export function useReportMenuItem() {
  const toast = useToast();

  return useMutation({
    mutationFn: (request: ReportMenuItemRequest) =>
      apiFetch<void>('/menu-item-reports/', {
        method: 'POST',
        body: request,
      }),
    onSuccess: () => {
      toast({
        title: 'Report submitted. Thanks for keeping Paire accurate.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        title: 'Something went wrong. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });
}

/**
 * Hook to report a restaurant profile page via POST /restaurant-reports/
 * ("Report This Page", paire-app#106). Mirrors useReportMenuItem: toasts on
 * completion, no cache invalidation (nothing member-visible changes).
 * A 200 (this member already has the same report open) is success too.
 */
export function useReportRestaurant() {
  const toast = useToast();

  return useMutation({
    mutationFn: (request: ReportRestaurantRequest) =>
      apiFetch<void>('/restaurant-reports/', {
        method: 'POST',
        body: request,
      }),
    onSuccess: () => {
      toast({
        title: 'Report submitted. Thanks for keeping Paire accurate.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        title: 'Something went wrong. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });
}

// ============ Notifications ============

/**
 * Hook to mark a notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) =>
      apiFetch<Notification>(`/social/notifications/${notificationId}/read/`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.notifications() });
    },
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<{ message: string; count: number }>('/social/notifications/mark-all-read/', {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.notifications() });
    },
  });
}

/**
 * Hook to delete a notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) =>
      apiFetch<void>(`/social/notifications/${notificationId}/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.notifications() });
      queryClient.invalidateQueries({ queryKey: socialKeys.unreadCount() });
    },
  });
}

/**
 * Hook to delete all notifications
 */
export function useDeleteAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<{ message: string; count: number }>('/social/notifications/delete-all/', {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.notifications() });
      queryClient.invalidateQueries({ queryKey: socialKeys.unreadCount() });
    },
  });
}

/**
 * Hook to update notification preferences
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: Partial<NotificationPreferences>) =>
      apiFetch<NotificationPreferences>('/social/notifications/preferences/', {
        method: 'PATCH',
        body: preferences,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.preferences() });
    },
  });
}

// ============ Stories ============

/**
 * Hook to create a new story
 */
export function useCreateStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storyData: CreateStoryRequest) =>
      apiFetch<Story>('/social/stories/', {
        method: 'POST',
        body: storyData,
      }),
    onSuccess: (_, variables) => {
      // Track story created
      analytics.capture(AnalyticsEvents.STORY_CREATED, {
        has_restaurant: !!variables.restaurant,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.stories() });
    },
  });
}

/**
 * Hook to delete a story
 */
export function useDeleteStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storyId: number) =>
      apiFetch<void>(`/social/stories/${storyId}/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: socialKeys.stories() });
    },
  });
}

/**
 * Hook to mark a story as viewed
 */
export function useViewStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storyId: number) =>
      apiFetch<{ viewed: boolean; viewCount: number }>(`/social/stories/${storyId}/view/`, {
        method: 'POST',
      }),
    onSuccess: (_, storyId) => {
      // Track story viewed
      analytics.capture(AnalyticsEvents.STORY_VIEWED, {
        story_id: storyId,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.stories() });
    },
  });
}

// ============ Polls ============

/**
 * Hook to vote on a poll
 */
export function useVotePoll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, optionId }: { urlKey: string; optionId: number }) =>
      apiFetch<{ message: string; optionId: number }>(`/social/posts/${urlKey}/poll/vote/`, {
        method: 'POST',
        body: { option_id: optionId },
      }),
    onSuccess: (_, variables) => {
      // Track poll voted
      analytics.capture(AnalyticsEvents.POLL_VOTED, {
        post_url_key: variables.urlKey,
        option_id: variables.optionId,
      });

      queryClient.invalidateQueries({ queryKey: socialKeys.posts() });
      queryClient.invalidateQueries({ queryKey: socialKeys.feed() });
    },
  });
}

// ============ Contacts ============

/**
 * Hook to match email addresses from phone contacts against registered users
 */
export function useMatchContacts() {
  return useMutation({
    mutationFn: (emails: string[]) =>
      apiFetch<{ matches: UserSearchResult[] }>('/social/users/contacts/match/', {
        method: 'POST',
        body: { emails },
      }),
  });
}

// ============ Phone Verification ============

/**
 * Hook to send OTP for phone verification
 */
export function useSendPhoneOTP() {
  return useMutation({
    mutationFn: (data: SendOTPRequest) =>
      apiFetch<SendOTPResponse>('/social/users/phone/send-otp/', {
        method: 'POST',
        body: data,
      }),
  });
}

/**
 * Hook to verify phone OTP
 */
export function useVerifyPhoneOTP() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VerifyOTPRequest) =>
      apiFetch<VerifyOTPResponse>('/social/users/phone/verify-otp/', {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      // Invalidate user data to reflect phone verification
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

/**
 * Hook to match contacts by email AND phone numbers
 */
export function useMatchContactsAll() {
  return useMutation({
    mutationFn: (data: ContactsMatchAllRequest) =>
      apiFetch<ContactsMatchAllResponse>('/social/users/contacts/match-all/', {
        method: 'POST',
        body: data,
      }),
  });
}
